import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { BoxHeader } from "@/components/design/BoxHeader";
import { PowerTile } from "@/components/design/PowerTile";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { NoBackend } from "@/components/common/NoBackend";
import { ELEMENT_CODES, ELEMENT_GROUPS, GROUP_ORDER, elementDisplayName } from "@/lib/elements";
import { POWER_BY_ABBR } from "@/lib/power-library";
import { usePowersData } from "../use-powers-data";

// Every user implicitly has all 30 powers; the taxonomy comes statically
// from lib/elements. selfAssessmentResults provides per-power measured
// level (0–100, null = not yet evaluated); unmeasured powers still render
// (inactive, no score). Same data source as Save · Powers.
export function PowersSidebar({
  onNavigate,
  showAllLink = true,
  showScores = true,
  linkElements = false,
}: {
  onNavigate: (path: string) => void;
  showAllLink?: boolean;
  // Foreign profiles can't yet load the viewed person's own powers (genesis
  // has no person-scoped powers query). Until then we render the power tiles
  // as plain boxes without a rating instead of showing the viewer's values.
  showScores?: boolean;
  // Own profile only: make each power tile a link to its detail page
  // (/grow/power/:abbr), so the owner can drill into their own powers.
  // Foreign /p/ profiles pass false → tiles stay non-interactive.
  //
  // Deliberately decoupled from showScores. When the person-scoped powers
  // API lands, /p/ will set showScores=true to DISPLAY a viewed person's
  // (audience-gated) scores — but linkElements MUST stay false there: the
  // detail page is the owner's personal workspace (own self-assessment,
  // evidence from own documents) and is principal-scoped (PowersData takes
  // no person argument). Never gate both on one flag, or showing foreign
  // scores would silently re-enable detail-page access.
  linkElements?: boolean;
}) {
  const intl = useIntl();
  const { selfByElement, smartByElement, loading, error } = usePowersData({ skip: !showScores });
  // Which evaluation the tiles display; same switch as the Power Map.
  const [scoreSource, setScoreSource] = useState<"self" | "smart">("smart");
  const levelByElement = showScores
    ? scoreSource === "self"
      ? selfByElement
      : smartByElement
    : new Map<string, number | null | undefined>();

  return (
    <div className="box bg-surface p-4">
      <BoxHeader
        title={intl.formatMessage({ id: "profile.sidebars.powersTitle", defaultMessage: "Powers" })}
        right={
          showScores ? (
            <SegmentedControl<"smart" | "self">
              size="sm"
              value={scoreSource}
              onChange={setScoreSource}
              options={[
                {
                  value: "smart",
                  label: intl.formatMessage({
                    id: "profile.sidebars.smartAnalysis",
                    defaultMessage: "Smart analysis",
                  }),
                },
                {
                  value: "self",
                  label: intl.formatMessage({
                    id: "profile.sidebars.selfAssessment",
                    defaultMessage: "Self-assessment",
                  }),
                },
              ]}
            />
          ) : undefined
        }
      />

      {showAllLink ? (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => onNavigate("/grow/map")}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-ink/45 transition hover:text-accent"
          >
            <FormattedMessage id="profile.sidebars.powerMap" defaultMessage="Power Map" />{" "}
            <span aria-hidden="true">↗</span>
          </button>
        </div>
      ) : null}

      {showScores && (loading || error) ? (
        <div className="mb-3 text-[11px] text-ink/45">
          {loading ? (
            <FormattedMessage id="profile.sidebars.loading" defaultMessage="loading…" />
          ) : (
            <FormattedMessage
              id="profile.sidebars.errorPrefix"
              defaultMessage="Error: {message}"
              values={{ message: error ? userErrorMessage(intl, error) : "" }}
            />
          )}
        </div>
      ) : null}

      <div className="space-y-4">
        {GROUP_ORDER.map((g) => {
          const group = ELEMENT_GROUPS[g];
          return (
            <div key={g}>
              <div className="field-value mb-2 font-medium text-ink/80">{intl.formatMessage({ id: group.labelId })}</div>
              {/* Fixed-size wrapping tiles (not a fixed column count) so each
                  tile stays the same compact size as the Grow · Power Map ones
                  (~100px) regardless of how wide this sidebar gets — a
                  fixed-column grid stretched them to fill the wide/full-width
                  column. Mirrors the auto-fill pattern in ServicePage. */}
              <div className="grid justify-start gap-2 [grid-template-columns:repeat(auto-fill,100px)]">
                {group.elements.map((el) => {
                  const code = ELEMENT_CODES[el as keyof typeof ELEMENT_CODES];
                  const p = code ? POWER_BY_ABBR[code] : undefined;
                  const level = levelByElement.get(el);
                  return (
                    // Own profile (linkElements): each tile links to its power
                    // detail (/grow/power/:abbr), mirroring the Grow Power Map
                    // (PowerLibraryPage). Foreign /p/ profiles pass
                    // linkElements=false → no onSelect → non-interactive card.
                    <PowerTile
                      key={el}
                      variant="score"
                      abbr={code ?? ""}
                      name={p ? intl.formatMessage({ id: p.nameId }) : elementDisplayName(el)}
                      // Uniform card style regardless of source/measured;
                      // only the number appears/changes when toggling.
                      active={false}
                      score={level != null ? level : null}
                      onSelect={
                        linkElements && code ? () => onNavigate(`/grow/power/${code}`) : undefined
                      }
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function FavoritesSidebar() {
  const intl = useIntl();
  const favoritesLabel = intl.formatMessage({
    id: "profile.sidebars.favorites",
    defaultMessage: "Favorites",
  });
  return (
    <div className="box bg-surface p-4">
      <BoxHeader title={favoritesLabel} />
      <NoBackend label={favoritesLabel} comingSoon />
    </div>
  );
}
